import React, { useEffect, useState } from "react";
import { apiClient } from "../utils/const";
import DeleteUserButton from "./DeleteButton";
import EditCustomer from "./EditCustomer";
import AddCustomer from "./AddCustomer";
import Swal from "sweetalert2";

const safeNumber = (value, fallback = 0) => {
  const num = Number(value);
  return isNaN(num) ? fallback : num;
};

const CustomerList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const [schemeType, setSchemeType] = useState(
    () => localStorage.getItem("schemeType") || "mandal"
  );

  const fetchUsers = () => {
    setLoading(true);
    setError(null);

    const endpoint =
      schemeType === "kanchan"
        ? "kanchan/kanchan_users_data.php"
        : "mandal/mandal_users_data.php";

    apiClient
      .post(endpoint)
      .then((res) => {
        // console.log(res.data)
        if (
          res.data &&
          res.data.success &&
          Array.isArray(res.data.data?.users)
        ) {
        setUsers(res.data.data.users);
        } else {
          setError(res.data?.message || "Unexpected response format");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Network error");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, [schemeType]);

  useEffect(() => {
    const handleSchemeChange = () => {
      setSchemeType(localStorage.getItem("schemeType") || "mandal");
    };
    window.addEventListener("schemeChanged", handleSchemeChange);
    return () => window.removeEventListener("schemeChanged", handleSchemeChange);
  }, []);

  const getFullName = (user) => {
    return [user.first_name, user.middle_name, user.last_name]
      .filter(Boolean)
      .join(" ");
  };

  const filteredUsers = users.filter((user) => {
    const name = getFullName(user);
    const phone = user.phone || "";
    const matchesSearch = name.includes(search) || phone.includes(search);
    
    // Status filtering
    let matchesStatus = true;
    if (statusFilter === "Active") {
      matchesStatus = Number(user.status) === 1;
    } else if (statusFilter === "Withdrawn") {
      matchesStatus = Number(user.status) === 2;
    }
    // "All" status filter matches all users
    
    
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / entriesPerPage) || 1;
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  return (
    <div className="page-wrapper" style={{height: '100%', overflow: 'hidden'}}>
      <div className="page-content" style={{height: '100%', overflow: 'hidden'}}>
        <div className="container-xxl">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <h1
                        className="card-title"
                        style={{ fontSize: "1.5rem", fontWeight: "bold" }}
                      >
                        Customer Details
                      </h1>
                    </div>
                    <div className="col-auto d-flex align-items-center">
                      <button
                        className="btn"
                        onClick={() => setShowAddModal(true)}
                        style={{
                          backgroundColor: "#4B2C3B",
                          borderColor: "#4B2C3B",
                          color: "#fff",
                        }}
                      >
                        <i className="fas fa-plus me-1" /> Add Customer
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card-body pt-0">
                  <div
                    className="d-flex justify-content-between align-items-center mb-3"
                    style={{ marginTop: "1rem" }}
                  >
                    <div>
                      <select
                        value={entriesPerPage}
                        onChange={(e) => {
                          setEntriesPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="form-select d-inline-block w-auto"
                      >
                        {[5, 10, 20, 50].map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                      </select>
                      <span className="ms-2">entries per page</span>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <select
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="form-select d-inline-block w-auto"
                        style={{ minWidth: 120 }}
                      >
                        <option value="All">All</option>
                        <option value="Active">Active</option>
                        <option value="Withdrawn">Withdrawn</option>
                      </select>
                      <input
                        type="text"
                        className="form-control w-auto"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCurrentPage(1);
                        }}
                        style={{ minWidth: 200 }}
                      />
                    </div>
                  </div>

                  <div className="table-responsive">
                    {loading ? (
                      <div className="text-center p-4">Loading...</div>
                    ) : error ? (
                      <div className="alert alert-danger m-3">{error}</div>
                    ) : (
                      <table className="table mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center px-3 py-3 border-0">ID</th>
                            <th className="text-start px-3 py-3 border-0">Name</th>
                            <th className="text-center px-3 py-3 border-0">Phone</th>
                            <th className="text-center px-3 py-3 border-0">Total Paid</th>
                            <th className="text-center px-3 py-3 border-0">Last Paid</th>
                            {schemeType === "kanchan" && <th className="text-center px-3 py-3 border-0">Gold</th>}
                            <th className="text-center px-3 py-3 border-0">Progress</th>
                            <th className="text-center px-3 py-3 border-0">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.map((user, idx) => {
                            const monthsPaid = safeNumber(
                              11 - user.months_left,
                              0
                            );
                            const totalMonths = safeNumber(
                              user.scheme_total_months,
                              0
                            );
                            const totalPaid = safeNumber(
                              Number(user.total_paid || 0).toFixed(2),
                              "0.00"
                            );
                            const monthsPaidGold = Math.min(
                              safeNumber(user.months_paid || 0, 0),
                              totalMonths
                            );
                            const totalGoldGms = safeNumber(
                              user.total_gold_gms || 0,
                              0
                            );
                            const isGoldScheme = schemeType === "kanchan";

                            return (
                              <tr
                                key={user.id || idx}
                                className="border-bottom"
                                style={{
                                  borderBottom:
                                    "1px solid var(--bs-border-color)",
                                  transition: "all 0.3s ease",
                                  backgroundColor:
                                    idx % 2 === 0
                                      ? "transparent"
                                      : "var(--bs-gray-50)",
                                }}
                                onMouseEnter={(e) => {
                                  const tr = e.target.closest("tr");
                                  tr.style.backgroundColor =
                                    "var(--bs-primary-bg-subtle)";
                                  tr.style.transform = "translateY(-1px)";
                                  tr.style.boxShadow =
                                    "0 4px 20px rgba(0,0,0,0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  const tr = e.target.closest("tr");
                                  const idx = Array.from(
                                    tr.parentNode.children
                                  ).indexOf(tr);
                                  tr.style.backgroundColor =
                                    idx % 2 === 0
                                      ? "transparent"
                                      : "var(--bs-gray-50)";
                                  tr.style.transform = "translateY(0px)";
                                  tr.style.boxShadow = "none";
                                }}
                              >
                                {/* Card Number Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="badge bg-primary bg-gradient px-2 py-1"
                                    style={{
                                      fontSize: "0.8rem",
                                      fontWeight: "600",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    {`#${user.card_no || user.id || idx + 1}`}
                                  </span>
                                </td>

                                {/* Name Column */}
                                <td className="px-3 py-3 border-0">
                                  <div className="text-start">
                                    <h6
                                      className="mb-0 fw-semibold"
                                      style={{
                                        fontSize: "0.95rem",
                                        color: "var(--bs-body-color)",
                                      }}
                                    >
                                      {getFullName(user)}
                                    </h6>
                                  </div>
                                </td>

                                {/* Phone Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="fw-medium"
                                    style={{
                                      fontSize: "0.9rem",
                                      color: "var(--bs-secondary-color)",
                                    }}
                                  >
                                    {user.phone}
                                  </span>
                                </td>

                                {/* Total Paid Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="badge bg-success bg-gradient px-3 py-2"
                                    style={{
                                      fontSize: "0.85rem",
                                      fontWeight: "600",
                                      borderRadius: "12px",
                                      boxShadow:
                                        "0 2px 8px rgba(40, 167, 69, 0.3)",
                                    }}
                                  >
                                    ₹{Number(totalPaid).toLocaleString("en-IN")}
                                  </span>
                                </td>

                                {/* Last Paid Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="fw-medium"
                                    style={{
                                      fontSize: "0.85rem",
                                      color: "var(--bs-secondary-color)",
                                    }}
                                  >
                                    {user.last_payment_date || "N/A"}
                                  </span>
                                </td>

                                {/* Gold Column (Kanchan only) */}
                                {isGoldScheme && (
                                  <td className="text-center px-3 py-3 border-0">
                                    <span
                                      className="badge bg-warning bg-gradient text-dark px-3 py-2"
                                      style={{
                                        fontSize: "0.85rem",
                                        fontWeight: "600",
                                        borderRadius: "12px",
                                        boxShadow:
                                          "0 2px 8px rgba(255, 193, 7, 0.3)",
                                      }}
                                    >
                                      {totalGoldGms}g
                                    </span>
                                  </td>
                                )}

                                {/* Progress Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <div className="d-flex flex-column align-items-center">
                                    {Number(user.status) === 2 ? (
                                      /* Withdrawn Status - No Progress Bar */
                                      <span
                                        className="badge bg-danger bg-gradient px-3 py-2"
                                        style={{
                                          fontSize: "0.8rem",
                                          fontWeight: "600",
                                          borderRadius: "10px",
                                          minWidth: "80px",
                                        }}
                                      >
                                        Withdrawn
                                      </span>
                                    ) : (
                                      <>
                                        {/* Progress Badge */}
                                        <span
                                          className={`badge mb-2 px-3 py-1 ${
                                            Number(user.status) === 0
                                              ? "bg-warning bg-gradient"
                                              : "bg-success bg-gradient"
                                          }`}
                                          style={{
                                            fontSize: "0.8rem",
                                            fontWeight: "600",
                                            borderRadius: "10px",
                                            minWidth: "55px",
                                          }}
                                        >
                                          {isGoldScheme
                                            ? `${monthsPaidGold}/${totalMonths}`
                                            : `${monthsPaid}/11`}
                                        </span>

                                        {/* Bootstrap Progress Bar */}
                                        <div
                                          className="progress"
                                          style={{ width: "90px", height: "6px" }}
                                        >
                                          <div
                                            className={`progress-bar progress-bar-striped ${
                                              Number(user.status) === 0
                                                ? "bg-warning"
                                                : "bg-success"
                                            }`}
                                            role="progressbar"
                                            style={{
                                              width: `${
                                                isGoldScheme
                                                  ? (monthsPaidGold / totalMonths) *
                                                    100
                                                  : (monthsPaid / 11) * 100
                                              }%`,
                                              borderRadius: "3px",
                                            }}
                                          ></div>
                                        </div>

                                        {/* Waiting Status */}
                                        {isGoldScheme &&
                                          monthsPaidGold === totalMonths &&
                                          user.wait_months > 0 && (
                                            <small
                                              className="badge bg-light text-primary mt-1 px-2 py-1"
                                              style={{
                                                fontSize: "0.7rem",
                                                borderRadius: "6px",
                                              }}
                                            >
                                              <i className="fas fa-clock me-1"></i>
                                              Wait: {user.wait_months}m
                                            </small>
                                          )}
                                      </>
                                    )}
                                  </div>
                                </td>

                                {/* Actions Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <div className="btn-group" role="group">
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm me-1"
                                      title="Edit Customer"
                                      onClick={() =>
                                        setEditingCustomerId(user.id)
                                      }
                                      style={{
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        transition: "all 0.2s ease",
                                        border: "1px solid #667eea",
                                        color: "#667eea",
                                        backgroundColor: "transparent",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          "#667eea";
                                        e.currentTarget.style.color = "white";
                                        e.currentTarget.style.transform =
                                          "translateY(-1px)";
                                        e.currentTarget.style.boxShadow =
                                          "0 4px 12px rgba(102, 126, 234, 0.3)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          "transparent";
                                        e.currentTarget.style.color = "#667eea";
                                        e.currentTarget.style.transform =
                                          "translateY(0px)";
                                        e.currentTarget.style.boxShadow =
                                          "none";
                                      }}
                                    >
                                      <i
                                        className="fas fa-edit"
                                        style={{ fontSize: "0.8rem" }}
                                      ></i>
                                    </button>

                                    <DeleteUserButton
                                      userId={user.id}
                                      userName={getFullName(user)}
                                      cardNo={user.card_no}
                                      onDeleteSuccess={(deletedId) =>
                                        setUsers((prev) =>
                                          prev.filter((u) => u.id !== deletedId)
                                        )
                                      }
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {!loading && !error && (
                    <div className="d-flex justify-content-center align-items-center mt-3">
                      <button
                        className="btn btn-outline-secondary btn-sm me-2"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Prev
                      </button>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className="btn btn-outline-secondary btn-sm ms-2"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        {editingCustomerId && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div
              className={`modal-dialog modal-lg modal-dialog-centered animate__animated animate__zoomIn animate__faster `}
            >
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ backgroundColor: "#4B2C3B", color: "white" }}
                >
                  <h5 className="modal-title mb-0">Edit Customer</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setEditingCustomerId(null)}
                  />
                </div>
                <div className="modal-body">
                  <EditCustomer
                    customerId={editingCustomerId}
                    onClose={() => {
                      setEditingCustomerId(null);
                      fetchUsers();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Modal */}
        {showAddModal && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div
              className={`modal-dialog modal-lg modal-dialog-centered animate__animated animate__zoomIn animate__faster`}
            >
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ backgroundColor: "#4B2C3B", color: "white" }}
                >
                  <h5 className="modal-title">Add Customer</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <AddCustomer
                    onClose={() => {
                      setShowAddModal(false);
                      fetchUsers();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerList;
